import { SkyMesh } from "three/addons/objects/SkyMesh.js"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

export class Sky3 {
  scene: THREE.Object3D
  debugFolder?: FolderApi

  /** The SkyMesh instance (Preetham skydome). */
  mesh: SkyMesh

  /** Sun direction derived from elevation/azimuth; update via updateSun(). */
  sun: THREE.Vector3

  /** Parameters used for sun position and optional exposure (for Tweakpane and updateSun). */
  parameters: {
    elevation: number
    azimuth: number
    exposure: number
  }

  private onExposureChange?: (value: number) => void

  constructor({
    scene,
    debugFolder,
    scale = 10000,
    turbidity = 10,
    rayleigh = 1.6,
    mieCoefficient = 0.004,
    mieDirectionalG = 0.4,
    elevation = 0,
    azimuth = 30,
    exposure = 1,
    cloudCoverage = 0.3,
    cloudDensity = 0.4,
    cloudElevation = 0.2,
    cloudScale = 0.0043,
    cloudSpeed = 0.0001,
    onExposureChange,
  }: {
    scene: THREE.Object3D
    debugFolder?: FolderApi
    scale?: number
    turbidity?: number
    rayleigh?: number
    mieCoefficient?: number
    mieDirectionalG?: number
    elevation?: number
    azimuth?: number
    exposure?: number
    cloudCoverage?: number
    cloudDensity?: number
    cloudElevation?: number
    cloudScale?: number
    cloudSpeed?: number
    /** Called when exposure changes in debug UI; use to set renderer.toneMappingExposure. */
    onExposureChange?: (value: number) => void
  }) {
    this.scene = scene
    this.debugFolder = debugFolder
    this.sun = new THREE.Vector3()
    this.parameters = { elevation, azimuth, exposure }
    this.onExposureChange = onExposureChange

    this.mesh = new SkyMesh()
    this.mesh.scale.setScalar(scale)
    this.mesh.turbidity.value = turbidity
    this.mesh.rayleigh.value = rayleigh
    this.mesh.mieCoefficient.value = mieCoefficient
    this.mesh.mieDirectionalG.value = mieDirectionalG
    this.mesh.cloudCoverage.value = cloudCoverage
    this.mesh.cloudDensity.value = cloudDensity
    this.mesh.cloudElevation.value = cloudElevation
    this.mesh.cloudScale.value = cloudScale
    this.mesh.cloudSpeed.value = cloudSpeed

    this.setMesh()
    this.updateSun()
    this.setDebug()
  }

  setMesh() {
    this.scene.add(this.mesh)
  }

  /** Updates sun direction from parameters.elevation and parameters.azimuth and writes to mesh.sunPosition. */
  updateSun() {
    const phi = THREE.MathUtils.degToRad(90 - this.parameters.elevation)
    const theta = THREE.MathUtils.degToRad(this.parameters.azimuth)
    this.sun.setFromSphericalCoords(1, phi, theta)
    this.mesh.sunPosition.value.copy(this.sun)
  }

  update() {}

  setDebug() {
    if (!this.debugFolder) return

    const folder = this.debugFolder.addFolder({
      title: "🌤️ Sky3 (SkyMesh)",
      expanded: true,
    })

    folder
      .addBinding(this.parameters, "elevation", { min: 0, max: 90, step: 0.1 })
      .on("change", () => this.updateSun())
    folder
      .addBinding(this.parameters, "azimuth", {
        min: -180,
        max: 180,
        step: 0.1,
      })
      .on("change", () => this.updateSun())
    folder
      .addBinding(this.parameters, "exposure", {
        min: 0,
        max: 1,
        step: 0.0001,
      })
      .on("change", (ev) => this.onExposureChange?.(ev.value))

    const folderAtmosphere = folder.addFolder({
      title: "Atmosphere (sky color)",
      expanded: true,
    })
    folderAtmosphere.addBinding(this.mesh.turbidity, "value", {
      label: "turbidity (↑ warmer/hazier)",
      min: 0,
      max: 20,
      step: 0.1,
    })
    folderAtmosphere.addBinding(this.mesh.rayleigh, "value", {
      label: "rayleigh (↑ more blue)",
      min: 0,
      max: 10,
      step: 0.01,
    })
    folderAtmosphere.addBinding(this.mesh.mieCoefficient, "value", {
      label: "mieCoefficient (sun halo)",
      min: 0,
      max: 0.1,
      step: 0.001,
    })
    folderAtmosphere.addBinding(this.mesh.mieDirectionalG, "value", {
      label: "mieDirectionalG",
      min: 0,
      max: 1,
      step: 0.01,
    })

    const folderClouds = folder.addFolder({
      title: "Clouds",
      expanded: true,
    })
    folderClouds.addBinding(this.mesh.cloudCoverage, "value", {
      label: "coverage",
      min: 0,
      max: 1,
      step: 0.01,
    })
    folderClouds.addBinding(this.mesh.cloudDensity, "value", {
      label: "density",
      min: 0,
      max: 1,
      step: 0.01,
    })
    folderClouds.addBinding(this.mesh.cloudElevation, "value", {
      label: "elevation",
      min: 0,
      max: 1,
      step: 0.01,
    })
    folderClouds.addBinding(this.mesh.cloudScale, "value", {
      label: "scale",
      min: 0,
      max: 0.01,
      step: 0.0001,
    })
    folderClouds.addBinding(this.mesh.cloudSpeed, "value", {
      label: "speed",
      min: 0,
      max: 0.001,
      step: 0.00001,
    })
  }
}
